import { ApiResponse } from '@/lib/api/api-response';
import { goApiClient } from '@/lib/api/base-client';
import { TransactionModel } from '../../types/TransactionModel';
import {
  CreateTransactionDTO,
  GetTransactionDTO,
  TransactionFilterOptionsDTO,
  UpdateTransactionDTO,
  TransactionStatus,
} from './transaction.contracts';
import { PagePaginationQueryDtoParams, PagePaginationResultDto } from '@/lib/api/pagination.page.dto';

export class TransactionService {
  public basePath = 'transaction';

  async create(transactionToCreate: CreateTransactionDTO): Promise<ApiResponse<TransactionModel>> {
    try {
      const applicationResponse = await goApiClient.post<GetTransactionDTO>(this.basePath + '/', transactionToCreate);
      return applicationResponse.processPayload(TransactionModel.fromDTO);
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async update(transactionId: number, transactionToUpdate: UpdateTransactionDTO): Promise<ApiResponse<undefined>> {
    try {
      const applicationResponse = await goApiClient.put<undefined>(`${this.basePath}/${transactionId}`, transactionToUpdate);
      return applicationResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async updateStatus(transactionId: number, status: TransactionStatus): Promise<ApiResponse<TransactionModel>> {
    try {
      const applicationResponse = await goApiClient.patch<GetTransactionDTO>(`${this.basePath}/${transactionId}/status`, { status });
      return applicationResponse.processPayload(TransactionModel.fromDTO);
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async bulkUpdateStatus(transactionIds: number[], status: TransactionStatus): Promise<ApiResponse<undefined>> {
    try {
      const applicationResponse = await goApiClient.post<undefined>(`${this.basePath}/status`, {
        status,
        transactionIds,
      });
      return applicationResponse;
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async get(
    query: TransactionFilterOptionsDTO,
    pagination: PagePaginationQueryDtoParams,
  ): Promise<ApiResponse<PagePaginationResultDto<TransactionModel>>> {
    try {
      const applicationResponse = await goApiClient.get<PagePaginationResultDto<GetTransactionDTO>>(this.basePath, {
        ...query,
        ...pagination,
      });
      return applicationResponse.processPagedPayload((payload) => {
        return payload.map(TransactionModel.fromDTO);
      });
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getAll(
    query: TransactionFilterOptionsDTO,
    pagination: PagePaginationQueryDtoParams = { limit: 1000 },
  ): Promise<ApiResponse<TransactionModel[]>> {
    try {
      const allTransactions: TransactionModel[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const applicationResponse = await goApiClient.get<PagePaginationResultDto<GetTransactionDTO>>(this.basePath, {
          ...query,
          ...pagination,
          page: currentPage,
        });

        if (!applicationResponse.isSuccess || !applicationResponse.payload) {
          return ApiResponse.UnknownErrorResponse();
        }

        const pageData = applicationResponse.payload.data.map(TransactionModel.fromDTO);
        allTransactions.push(...pageData);

        totalPages = applicationResponse.payload.totalPages;
        currentPage += 1;
      } while (currentPage <= totalPages);

      return ApiResponse.responseWithPayload(allTransactions, 200);
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }

  async getById(id: number): Promise<ApiResponse<TransactionModel>> {
    try {
      const applicationResponse = await goApiClient.get<GetTransactionDTO>(`${this.basePath}/${id}`);
      return applicationResponse.processPayload(TransactionModel.fromDTO);
    } catch (error) {
      return ApiResponse.UnknownErrorResponse();
    }
  }
}

export const transactionService = new TransactionService();
